/**
 * Hooks de dados do editor de Playbooks (Brief 4 · Tela 1).
 *
 * Consome o CRUD real (apps/engine/src/modules/playbooks):
 *   - GET /v1/playbooks      (lista)   · gate `playbook-read`
 *   - GET /v1/playbooks/:id  (detalhe) · gate `playbook-read`
 *
 * As queries são GATEADAS por capability no `enabled` (não só no render) —
 * sem `playbook-read` o fetch nem dispara, evitando 403 vazando em toast.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useCapability } from '../../hooks/use-capability';
import { api } from '../../lib/api-client';
import { CONDUCT_PROFILE_QUERY_KEY } from '../conduct/use-conduct-profile';

import type {
  ChecklistTemplateDto,
  ChecklistTemplateSummary,
  CreatePlaybookBody,
  PlaybookDto,
  UpdatePlaybookBody,
} from './types';
import type { ProposalMetricsWire } from '../../lib/api-client';
import type { UseMutationResult } from '@tanstack/react-query';

const LIST_QUERY_KEY = 'playbooks-list';
const DETAIL_QUERY_KEY = 'playbook-detail';
const CHECKLIST_TEMPLATE_QUERY_KEY = 'playbook-checklist-template';
const CHECKLIST_TEMPLATE_LIST_QUERY_KEY = 'playbook-checklist-template-list';
const STALE_TIME = 60_000;

export function usePlaybookList(): {
  data: PlaybookDto[];
  isLoading: boolean;
  isError: boolean;
  canRead: boolean;
} {
  const canRead = useCapability('playbook-read');
  const query = useQuery({
    queryKey: [LIST_QUERY_KEY],
    queryFn: () => api.listPlaybooks(),
    enabled: canRead,
    staleTime: STALE_TIME,
    retry: 1,
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    canRead,
  };
}

export function usePlaybook(id: string | null): {
  data: PlaybookDto | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const canRead = useCapability('playbook-read');
  const query = useQuery({
    queryKey: [DETAIL_QUERY_KEY, id],
    queryFn: () => api.getPlaybook(id!),
    enabled: canRead && id !== null,
    staleTime: STALE_TIME,
    retry: 1,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/**
 * Resolve a checklist vinculada (aba Checklist). A query é GATEADA no `enabled`
 * por `checklists-basic` E por `templateId !== null` — sem cap ou sem vínculo o
 * fetch nem dispara (evita 403 vazando + respeita honest-defer). `canRead`
 * permite à UI distinguir "sem permissão" de "sem checklist".
 */
export function useChecklistTemplate(templateId: string | null): {
  data: ChecklistTemplateDto | undefined;
  isLoading: boolean;
  isError: boolean;
  canRead: boolean;
} {
  const canRead = useCapability('checklists-basic');
  const query = useQuery({
    queryKey: [CHECKLIST_TEMPLATE_QUERY_KEY, templateId],
    queryFn: () => api.getChecklistTemplate(templateId!),
    enabled: canRead && templateId !== null,
    staleTime: STALE_TIME,
    retry: 1,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    canRead,
  };
}

/**
 * Mutation de edição do playbook (PATCH /v1/playbooks/:id · gate `playbook-author`).
 *
 * Em sucesso: grava o DTO retornado no cache do detalhe (já com `version`
 * incrementada para o próximo If-Match) e invalida a lista. Erros (incl. 409 de
 * optimistic lock) propagam para o chamador tratar — a view exibe banner de
 * conflito não-destrutivo + recarregar (nunca sobrescreve cego).
 */
export function useUpdatePlaybook(
  id: string,
): UseMutationResult<PlaybookDto, unknown, { expectedVersion: number; body: UpdatePlaybookBody }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ expectedVersion, body }) => api.updatePlaybook(id, expectedVersion, body),
    onSuccess: (updated) => {
      queryClient.setQueryData([DETAIL_QUERY_KEY, id], updated);
      void queryClient.invalidateQueries({ queryKey: [LIST_QUERY_KEY] });
      // ★ Fase 2 «Conduta e Contexto» — invalida a RESOLUÇÃO da conduta (efetivo + origem
      // por eixo). Achado na validação viva no nível Tenant: sem isto o «Hoje: …» de cada
      // eixo segue mostrando o valor ANTIGO depois de salvar, porque esta mutação só
      // reescreve o cache da própria entidade. Tela de conduta com efetivo velho é
      // exatamente a desonestidade que esta frente existe para matar.
      void queryClient.invalidateQueries({ queryKey: [CONDUCT_PROFILE_QUERY_KEY] });
    },
  });
}

/**
 * Mutation de criação (POST /v1/playbooks · gate `playbook-author`). Serve tanto
 * o "Novo playbook" quanto a duplicação client-side (payload espelhado). Em
 * sucesso invalida a lista; o chamador navega para o editor do novo id.
 */
export function useCreatePlaybook(): UseMutationResult<PlaybookDto, unknown, CreatePlaybookBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.createPlaybook(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [LIST_QUERY_KEY] });
    },
  });
}

/**
 * Mutation de arquivamento / soft-delete (DELETE /v1/playbooks/:id · gate
 * `playbook-author`). If-Match obrigatório (versão carregada). O playbook passa
 * a `deprecated` (não some). Invalida a lista em `onSettled` (sucesso E erro):
 * no sucesso reflete o novo status; no 409 de optimistic lock recarrega a lista
 * com a versão atual, para que uma nova tentativa use o If-Match correto (a view
 * mostra feedback de conflito e o usuário tenta de novo sem ficar preso na versão
 * stale).
 */
export function useArchivePlaybook(): UseMutationResult<
  void,
  unknown,
  { id: string; expectedVersion: number }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, expectedVersion }) => api.archivePlaybook(id, expectedVersion),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [LIST_QUERY_KEY] });
    },
  });
}

/**
 * Lista templates de checklist do tenant para o select de vínculo na edição
 * (GET /v1/checklist-templates · gate `checklists-basic`). Gateada no `enabled`
 * por capability E por `enabled` externo (só busca quando a aba de edição precisa).
 */
export function useChecklistTemplateList(enabled: boolean): {
  data: ChecklistTemplateSummary[];
  isLoading: boolean;
  canRead: boolean;
} {
  const canRead = useCapability('checklists-basic');
  const query = useQuery({
    queryKey: [CHECKLIST_TEMPLATE_LIST_QUERY_KEY],
    queryFn: () => api.listChecklistTemplates(),
    enabled: canRead && enabled,
    staleTime: STALE_TIME,
    retry: 1,
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    canRead,
  };
}

const PROPOSAL_METRICS_QUERY_KEY = 'proposal-metrics';

/**
 * Métricas de aceitação das propostas de condução do playbook (aba Autonomia,
 * Item 368 F2 Fatia C).
 *
 * Fetch GATEADO no `enabled` (feedback_gatear_fetch_nao_so_render): só dispara
 * com `playbook-read` E `playbookId` presente. A aba só monta quando ativa
 * (switch no editor), então não há fetch fora da aba.
 */
export function useProposalMetrics(playbookId: string | null): {
  data: ProposalMetricsWire | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const canRead = useCapability('playbook-read');
  const query = useQuery({
    queryKey: [PROPOSAL_METRICS_QUERY_KEY, playbookId],
    queryFn: () => api.proposalMetrics(playbookId!),
    enabled: canRead && playbookId !== null,
    staleTime: 30_000,
    retry: 1,
  });

  return {
    data: query.data?.data,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
